import type { ReactNode } from "react";
import * as React from "react";
import type { SelectOptionType } from "@onshopfront/components";
import type { Moment } from "moment";
import type { Override } from "../../../types/internal/utility";
import type { LocalDatabaseCustomer } from "../../_Generated/Sync.gen";
import EditPage, {
    createSearchDataSource,
    type EditPageSection,
    type EditSectionType,
    generateEditFieldSection,
    generateFieldName,
} from "../../Common/EditPage/EditPage";
import EditPageField, { type EditPageFieldValueType } from "../../Common/EditPage/EditPageField";
import RetractableInput from "../../Common/Forms/RetractableInput";
import PopupManager from "../../Common/Popups/PopupManager";
import type { DataSourceTables } from "../../Data/_Bases/DataSourceTables";
import { determineDataSource } from "../../Data/DataStores/DataSource";
import { withPermissions } from "../../Utilities/Domain/Users/Permissions";
import { CancellablePromise } from "../../Utilities/Generic/Promises";
import { isValidEmail } from "../../Utilities/Generic/Strings";
import FontAwesomeLight from "../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import type { Customer } from "./CustomerTypes";

interface Props {
    onChange: (field: string, value: EditPageFieldValueType) => Promise<void>;
    onSave: (event: React.MouseEvent) => void;
    customer: Override<Customer, {
        birthday: Moment | null;
        accountLimit: string;
        allowMarketingEmails: string;
    }>;
    onClose: (e: React.MouseEvent) => void;
    floatSave: boolean;
    permissions: {
        adjust_customer_loyalty: boolean;
    };
    fromLocal?: boolean;
}

interface State {
    showLoyalty: boolean;
    selectedOptions: Record<string, SelectOptionType<string>>;
    duplicateCode: boolean;
}

const sections: Record<string, Array<EditSectionType<CustomerEditForm>>> = {
    information: [{
        field    : "firstName",
        type     : "string",
        autoFocus: true,
    }, {
        field: "lastName",
        type : "string",
    }, {
        field: "company",
        type : "string",
    }, {
        field : "code",
        custom: "renderCode",
    }, {
        field     : "customerGroup",
        type      : "select",
        dataSource: {
            type  : "database",
            source: "customerGroups",
        },
    }, {
        field: "overrideCustomerGroup",
        type : "toggle",
    }],
    contact: [{
        field : "emails",
        custom: "renderEmail",
    }, {
        field: "phone",
        type : "string",
    }, {
        field: "mobile",
        type : "string",
    }, {
        field: "fax",
        type : "string",
    }, {
        field     : "allowMarketingEmails",
        type      : "select",
        dataSource: {
            type  : "function",
            source: "getMarketingEmailRules",
        },
        clearable: false,
    }],
    social: [{
        field: "website",
        label: "Website",
        type : "string",
    }, {
        field: "twitter",
        label: "Twitter",
        type : "string",
    }, {
        field: "facebook",
        label: "Facebook",
        type : "string",
    }],
    billing: [{
        field: "billing.addressStreet1",
        label: "Street Address 1",
        type : "string",
    }, {
        field: "billing.addressStreet2",
        label: "Street Address 2",
        type : "string",
    }, {
        field: "billing.addressSuburb",
        label: "Suburb",
        type : "string",
    }, {
        field: "billing.addressPostcode",
        label: "Postcode / ZIP Code",
        type : "string",
    }, {
        field: "billing.addressRegion",
        label: "State / Province",
        type : "string",
    }, {
        field: "billing.addressCountry",
        label: "Country",
        type : "string",
    }],
    delivery: [{
        field: "delivery.addressStreet1",
        label: "Street Address 1",
        type : "string",
    }, {
        field: "delivery.addressStreet2",
        label: "Street Address 2",
        type : "string",
    }, {
        field: "delivery.addressSuburb",
        label: "Suburb",
        type : "string",
    }, {
        field: "delivery.addressPostcode",
        label: "Postcode / ZIP Code",
        type : "string",
    }, {
        field: "delivery.addressRegion",
        label: "State / Province",
        type : "string",
    }, {
        field: "delivery.addressCountry",
        label: "Country",
        type : "string",
    }],
    loyalty: [{
        field : "loyaltyEnabled",
        type  : "toggle",
        hidden: (_, currentClass): boolean => !currentClass.props.customer.overrideCustomerGroup,
    }, {
        field: "loyaltyPoints",
        type : "number",
    }],
    account: [{
        field      : "accountLimit",
        type       : "currency",
        placeholder: "Unlimited",
    }, {
        field   : "currentOwing",
        type    : "currency",
        disabled: true,
    }, {
        field: "requireOrderReference",
        type : "toggle",
    }],
    pricing: [{
        field: "disablePromotions",
        type : "toggle",
    }, {
        field     : "priceList",
        type      : "select",
        dataSource: {
            type  : "database",
            source: "priceLists",
        },
    }],
    miscellaneous: [{
        field: "businessNumber",
        type : "string",
    }, {
        field: "birthday",
        type : "date",
    }, {
        field     : "gender",
        type      : "select",
        clearable : false,
        dataSource: {
            type  : "function",
            source: "getGender",
        },
    }, {
        field: "invoiceMessage",
        type : "text",
        label: "Invoice and Statement Message",
    }, {
        field: "comments",
        type : "text",
        label: "Comments (Internal)",
    }],
};

class CustomerEditForm extends React.Component<Props, State> {
    protected codePromise: CancellablePromise<Array<LocalDatabaseCustomer>> | undefined;

    constructor(props: Props) {
        super(props);

        this.handleCodeChange = this.handleCodeChange.bind(this);
        this.handleEmailChange = this.handleEmailChange.bind(this);
        this.handleNewEmail = this.handleNewEmail.bind(this);
        this.handleRemoveEmail = this.handleRemoveEmail.bind(this);
        this.handleSave = this.handleSave.bind(this);

        this.state = {
            selectedOptions: {},
            showLoyalty    : false,
            duplicateCode  : false,
        };
    }

    public componentDidMount(): void {
        this.checkSeeLoyalty();
    }

    public componentDidUpdate(prevProps: Readonly<Props>): void {
        if(
            prevProps.customer.overrideCustomerGroup !== this.props.customer.overrideCustomerGroup ||
            prevProps.customer.customerGroup !== this.props.customer.customerGroup
        ) {
            this.checkSeeLoyalty();
        }
    }

    public componentWillUnmount(): void {
        if(this.codePromise) {
            this.codePromise.cancel();
        }
    }

    protected async checkSeeLoyalty(): Promise<void> {
        if(this.props.customer.overrideCustomerGroup || !this.props.customer.customerGroup) {
            this.setState({
                showLoyalty: true,
            });
        } else {
            try {
                const source = await determineDataSource();
                const customerGroup = await source.customerGroups.get(this.props.customer.customerGroup);

                this.setState({
                    showLoyalty: customerGroup.loyalty,
                });
            } catch(err) {
                console.error("Error loading customer group", err);
                this.setState({
                    showLoyalty: false,
                });
            }
        }
    }

    protected getGender(search: string, searchValue = false): Array<{ label: string; value: string; }> {
        const genders = {
            male  : "Male",
            female: "Female",
            ""    : "Not Specified",
        };

        const keyMap = [];
        const valueMap = [];

        for(const i in genders) {
            if(!Object.prototype.hasOwnProperty.call(genders, i)) continue;

            keyMap.push(i);
            valueMap.push(genders[i as keyof typeof genders]);
        }

        let searchArray;

        if(searchValue) {
            searchArray = keyMap;
        } else {
            searchArray = valueMap;
        }

        const matching = [];

        for(let i = 0, l = searchArray.length; i < l; i++) {
            if(searchArray[i].toLowerCase().includes(search.toLowerCase())) {
                matching.push(i);
            }
        }

        const results = [];

        for(let i = 0, l = matching.length; i < l; i++) {
            results.push({
                label: valueMap[matching[i]],
                value: keyMap[matching[i]],
            });
        }

        return results;
    }

    protected getMarketingEmailRules(search: string, searchValue = false): Array<{ label: string; value: string; }> {
        const marketingEmailRules = {
            optIn       : "Opted In",
            optOut      : "Opted Out",
            notSpecified: "Not Specified",
        };

        const keyMap = [];
        const valueMap = [];

        for(const i in marketingEmailRules) {
            if(!Object.prototype.hasOwnProperty.call(marketingEmailRules, i)) continue;

            keyMap.push(i);
            valueMap.push(marketingEmailRules[i as keyof typeof marketingEmailRules]);
        }

        let searchArray;

        if(searchValue) {
            searchArray = keyMap;
        } else {
            searchArray = valueMap;
        }

        const matching = [];

        for(let i = 0, l = searchArray.length; i < l; i++) {
            if(searchArray[i].toLowerCase().includes(search.toLowerCase())) {
                matching.push(i);
            }
        }

        const results = [];

        for(let i = 0, l = matching.length; i < l; i++) {
            results.push({
                label: valueMap[matching[i]],
                value: keyMap[matching[i]],
            });
        }

        return results;
    }

    protected handleCodeChange(e: React.FormEvent<HTMLInputElement>): void {
        const value = e.currentTarget.value;

        this.props.onChange("code", value);

        if(this.codePromise) {
            this.codePromise.cancel();
        }

        if(!value.trim()) {
            this.setState({
                duplicateCode: false,
            });

            return;
        }

        this.codePromise = new CancellablePromise(
            determineDataSource()
                .then(source => {
                    return source.customers.search(value, {
                        name          : false,
                        code          : true,
                        email         : false,
                        phone         : false,
                        company       : false,
                        customerGroups: [],
                        exactMatch    : true,
                        sort          : false,
                    });
                })
                .then(customers => {
                    if(this.props.customer) {
                        return customers.filter(customer => customer.uuid !== this.props.customer.id);
                    }

                    return customers;
                })
        );

        this.codePromise
            .promise
            .then(customers => {
                this.setState({
                    duplicateCode: customers.length !== 0,
                });
            })
            .catch(err => {
                if(!err.cancelled) {
                    console.error("Error checking customer code", err);
                }
            });
    }

    protected renderCode = (field: EditSectionType): React.ReactNode => {
        return (
            <div
                className={"edit-page-section-field" + (this.state.duplicateCode ? " duplicate-customer-code" : "")}
                key={field.field}
            >
                <RetractableInput
                    hasValue={!!this.props.customer[field.field as keyof Customer]}
                    label={"Code" + (this.state.duplicateCode ? " (A customer already exists with this code)" : "")}
                >
                    {(onFocus, onBlur): React.ReactNode => (
                        <input
                            type="text"
                            onChange={this.handleCodeChange}
                            value={(this.props.customer[field.field as keyof Customer] || "") as string}
                            onFocus={onFocus}
                            onBlur={onBlur}
                            placeholder=""
                        />
                    )}
                </RetractableInput>
            </div>
        );
    };

    protected handleEmailChange(e: React.ChangeEvent<HTMLInputElement>): void {
        const index = parseInt(e.currentTarget.dataset.index as string);
        const field = e.currentTarget.dataset.field as "emails";

        const emails = [
            ...this.props.customer[field],
        ];

        emails[index] = e.currentTarget.value;

        this.props.onChange(field, emails);
    }

    protected handleNewEmail(e: React.MouseEvent<HTMLInputElement>): void {
        const field = e.currentTarget.dataset.field as "emails";

        const emails = [
            ...this.props.customer[field],
        ];

        emails.push("");

        this.props.onChange(field, emails);
    }

    protected handleRemoveEmail(e: React.MouseEvent<HTMLInputElement>): void {
        const index = parseInt(e.currentTarget.dataset.index as string);
        const field = e.currentTarget.dataset.field as "emails";

        const emails = [
            ...this.props.customer[field].slice(0, index),
            ...this.props.customer[field].slice(index + 1),
        ];

        this.props.onChange(field, emails);
    }

    protected renderEmail = (field: EditSectionType): React.ReactNode => {
        return (
            <div
                className="edit-page-section-field edit-page-section-email edit-page-section-field-array"
                key={field.field}
            >
                <div className="edit-page-section-field-array-label">
                    <div className="edit-page-section-field-array-label-text">
                        <span>Emails</span>
                    </div>
                    <div
                        className="edit-page-section-field-array-label-add"
                        data-field={field.field}
                        onClick={this.handleNewEmail}
                    >
                        <span>Add</span>
                        <FontAwesomeLight
                            icon="plus"
                        />
                    </div>
                </div>
                <div className="customer-edit-emails">
                    {Array.isArray(this.props.customer[field.field as "emails"]) && (
                        this.props.customer[field.field as "emails"].map((email, index) => (
                            <div
                                className={
                                    "customer-edit-email edit-page-section-field-array-element" +
                                    " edit-page-section-field-trackable"
                                }
                                key={index}
                            >
                                <div className="edit-page-section-field-array-input">
                                    <input
                                        type="email"
                                        data-index={index}
                                        data-field={field.field}
                                        onChange={this.handleEmailChange}
                                        value={email}
                                    />
                                </div>
                                <div
                                    className="edit-page-section-field-array-remove"
                                    data-field={field.field}
                                    data-index={index}
                                    onClick={this.handleRemoveEmail}
                                >
                                    <FontAwesomeLight
                                        icon="times"
                                    />
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        );
    };
    protected async handleSave(e: React.MouseEvent): Promise<void> {
        if(this.props.customer.accountLimit === "") {
            await this.props.onChange("accountLimit", null);
        }

        // Validate the email addresses
        for(let i = 0, l = this.props.customer.emails.length; i < l; i++) {
            if(this.props.customer.emails[i].trim() === "") {
                continue;
            }

            if(!isValidEmail(this.props.customer.emails[i])) {
                PopupManager.error("An email address provided is invalid");

                return;
            }
        }

        this.props.onSave(e);
    }

    protected async lookupFieldValue(field: EditSectionType): Promise<void> {
        if(!this.props.customer[field.field as keyof Customer]) {
            return;
        } else if(typeof (this.props.customer[field.field as keyof Customer] as any).label !== "undefined") {
            return;
        } else if(typeof this.state.selectedOptions[field.field] !== "undefined") {
            return;
        } else if(!field.dataSource) {
            return;
        }

        let resultField: {
            label: string;
            value: string;
        };

        if(field.dataSource.type === "database") {
            const source = await determineDataSource();
            const result = await source[field.dataSource.source as keyof DataSourceTables].get(
                this.props.customer[field.field as keyof Customer] as never // lol
            ) as {
                name: string;
            };

            if(result) {
                resultField = {
                    label: result.name,
                    value: this.props.customer[field.field as keyof Customer] as string,
                };
            }
        } else {
            const fn = this[field.dataSource.source as keyof CustomerEditForm] as
                (search: string, searchValue: boolean) => Promise<Array<{
                    label: string;
                    value: string;
                }>>;

            const result = await fn(
                this.props.customer[field.field as keyof Customer] as string,
                true
            );

            if(result.length) {
                resultField = result[0];
            }
        }

        this.setState(prevState => {
            return {
                selectedOptions: {
                    ...prevState.selectedOptions,
                    [field.field]: resultField,
                },
            };
        });
    }

    protected generateSection(section: string, ignoreFields?: Array<string>): React.ReactNode {
        const fields = sections[section];

        return (
            <React.Fragment>
                {fields.map(field => {
                    if(ignoreFields && ignoreFields.includes(field.field)) {
                        return;
                    }

                    if(field.custom) {
                        const fn = this[field.custom as keyof CustomerEditForm] as
                            (field: EditSectionType) => ReactNode;

                        return fn(field);
                    }

                    let dataSource;

                    if(field.dataSource && field.dataSource.type === "database") {
                        dataSource = createSearchDataSource(field.dataSource.source);
                    }

                    let loadOptions: undefined | ((search: string) => Promise<Array<SelectOptionType<string>>>);

                    if(typeof field.dataSource !== "undefined") {
                        loadOptions = field.dataSource.type === "function" ?
                            (
                                this[field.dataSource.source as keyof CustomerEditForm] as
                                    (search: string) => Promise<Array<SelectOptionType<string>>>
                            ) :
                            dataSource;

                        void this.lookupFieldValue(field);
                    }

                    return (
                        <EditPageField
                            label={typeof field.label === "undefined" ? generateFieldName(field.field) : field.label}
                            field={field.field}
                            value={this.props.customer[field.field as keyof Customer] as EditPageFieldValueType}
                            type={field.type as "string"}
                            onChange={this.props.onChange}
                            disabled={typeof field.disabled === "boolean" ? field.disabled : false}
                            key={field.field}
                            selectedOption={
                                this.state.selectedOptions[field.field] === null ?
                                    undefined :
                                    this.state.selectedOptions[field.field] as any
                            }
                            loadOptions={loadOptions}
                        />
                    );
                })}
            </React.Fragment>
        );
    }

    protected getSections(): Array<EditPageSection> {
        const args: [
            Record<string, SelectOptionType<string>>,
            Props["customer"],
            CustomerEditForm,
            (field: string, value: EditPageFieldValueType) => void,
        ] = [
            this.state.selectedOptions,
            this.props.customer,
            this,
            this.props.onChange,
        ];

        const informationIgnore = [];

        if(!this.props.customer.customerGroup) {
            informationIgnore.push("overrideCustomerGroup");
        }

        const editSections: Array<EditPageSection> = [{
            icon   : "info",
            name   : "General Information",
            content: generateEditFieldSection(
                sections.information,
                args[0],
                args[1],
                args[2],
                args[3],
                informationIgnore
            ),
        }, {
            icon   : "fax",
            name   : "Contact",
            content: generateEditFieldSection(
                sections.contact,
                ...args
            ),
        }, {
            icon   : "comment-alt",
            name   : "Social Media",
            content: generateEditFieldSection(
                sections.social,
                ...args
            ),
        }, {
            icon   : "money-bill",
            name   : "Billing Address",
            content: generateEditFieldSection(
                sections.billing,
                ...args
            ),
        }, {
            icon   : "shipping-fast",
            name   : "Delivery Address",
            content: generateEditFieldSection(
                sections.delivery,
                ...args
            ),
        }];

        if(this.state.showLoyalty || !this.props.customer.customerGroup) {
            if(this.props.permissions.adjust_customer_loyalty) {
                editSections.push({
                    icon   : "award",
                    name   : "Loyalty",
                    content: generateEditFieldSection(
                        sections.loyalty,
                        ...args
                    ),
                });
            }
        }

        if(this.props.customer.overrideCustomerGroup) {
            editSections.push({
                icon   : "address-card",
                name   : "Account",
                content: generateEditFieldSection(
                    sections.account,
                    ...args
                ),
            });
        }

        if(this.props.customer.overrideCustomerGroup || !this.props.customer.customerGroup) {
            editSections.push({
                icon   : "money-check-alt",
                name   : "Special Pricing",
                content: generateEditFieldSection(
                    sections.pricing,
                    ...args
                ),
            });
        }

        editSections.push({
            icon   : "box-full",
            name   : "Miscellaneous",
            content: generateEditFieldSection(
                sections.miscellaneous,
                ...args
            ),
        });

        return editSections;
    }

    public render(): React.ReactNode {
        return (
            <EditPage
                sections={this.getSections()}
                onSave={this.handleSave}
                title={`${this.props.customer.firstName} ${this.props.customer.lastName}`}
                onClose={this.props.onClose}
                type="Customer"
                floatSave={this.props.floatSave}
                saveDisabled={this.props.fromLocal ?? false}
            />
        );
    }
}

export default withPermissions(CustomerEditForm, [ "adjust_customer_loyalty" ]);
